'use client';

import { c as _c } from "react/compiler-runtime";
import { LayoutType, SpaceMemberRoles, Types } from '@shared/all/domains/spaces';
import toast from '@shared/utils/helpers/toast';
import useAuth from '@shared/utils/hooks/useAuth';
import useBoardRealtime from '@shared/utils/hooks/useBoardRealtime';
import useLocalPreference from '@shared/utils/hooks/useLocalPreference';
import useNavigator from '@shared/utils/hooks/useNavigator';
import useSpace from '@shared/utils/hooks/useSpace';
import { useQuery } from '@tanstack/react-query';
import { Image, List, Shield, UserCog } from 'lucide-react';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter, useSearchParams } from 'next/navigation';
import { type ComponentProps, useState } from 'react';
import { RunnerProvider } from '../../../hooks/useRunnerContext';
import { boardScope, spaceScope, useRegisterSearchScope } from '../../../hooks/useSearchScope';
import AppLayout from '../../../layouts/AppLayout/AppLayout';
import { client, type RouterOutputs, service } from '../../../utils/trpc';
import Error from '../../common/Error';
import Media from '../../common/Media';
import FactiiisList from '../../factiiis/FactiiisList';
import Posts from '../../posts/Posts';
import UsersList from '../../profile/UsersList';
import NoSpaceFound from '../../spaces/NoSpaceFound';
import Rules from '../../spaces/Rules';
import SpaceHeader from '../../spaces/SpaceHeader';
import SpaceInviteBanner from '../../spaces/SpaceInviteBanner';
import BoardLayout from './BoardLayout';
const BoardHistoryPanel = dynamic(() => import('./BoardHistoryPanel'), {
  ssr: false
});
type BoardTopBannerProps = Pick<ComponentProps<typeof SpaceHeader>, 'space' | 'uploads' | 'isHomePage' | 'isBoardLayout' | 'onHistoryClick'>;
const BoardTopBanner = t0 => {
  const $ = _c(14);
  const {
    space,
    uploads,
    isHomePage,
    isBoardLayout,
    onHistoryClick
  } = t0;
  let t1;
  if ($[0] !== space?.membership?.roles) {
    t1 = space?.membership?.roles.includes(SpaceMemberRoles.INVITED);
    $[0] = space?.membership?.roles;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isInvited = Boolean(t1);
  let t2;
  if ($[2] !== space) {
    t2 = <SpaceInviteBanner space={space} />;
    $[2] = space;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== isBoardLayout || $[5] !== isHomePage || $[6] !== isInvited || $[7] !== onHistoryClick || $[8] !== space || $[9] !== uploads) {
    t3 = <SpaceHeader isEmbed={true} isBoardLayout={isBoardLayout} uploads={uploads} space={space} isHomePage={isHomePage} hasBanner={isInvited} onHistoryClick={onHistoryClick} />;
    $[4] = isBoardLayout;
    $[5] = isHomePage;
    $[6] = isInvited;
    $[7] = onHistoryClick;
    $[8] = space;
    $[9] = uploads;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t2 || $[12] !== t3) {
    t4 = <div className="space-y-2">{t2}{t3}</div>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
};
type Props = {
  spaceSlug: string;
  isHomePage?: boolean;
  initialData?: RouterOutputs['spaces']['get'];
  initialError?: boolean;
  isEmbed: boolean;
};
const VIEW_PARAM_IS_BOARD: Record<string, boolean> = {
  board: true,
  space: false
};
const SpacePage = t0 => {
  const $ = _c(102);
  const {
    spaceSlug,
    isHomePage,
    initialData,
    initialError: t1,
    isEmbed
  } = t0;
  const initialError = t1 === undefined ? false : t1;
  const router = useRouter();
  const searchParams = useSearchParams();
  const {
    localPreferenceState
  } = useLocalPreference();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      client,
      service
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    currentUser
  } = useAuth(t2);
  const {
    navigatorState
  } = useNavigator();
  const {
    queryKey,
    isMember,
    addRule,
    editRule
  } = useSpace();
  let t3;
  if ($[1] !== spaceSlug) {
    t3 = async () => {
      const [error, result] = await client.spaces.get.query({
        slug: spaceSlug
      }, {
        context: {
          silenceErrors: true
        }
      });
      if (error) {
        throw error;
      }
      return result;
    };
    $[1] = spaceSlug;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = Boolean(spaceSlug) && !initialError && currentUser.isAuthenticated !== null;
  const t5 = !initialData;
  let t6;
  if ($[3] !== initialData || $[4] !== queryKey || $[5] !== t3 || $[6] !== t4 || $[7] !== t5) {
    t6 = {
      queryKey,
      queryFn: t3,
      enabled: t4,
      refetchOnMount: t5,
      initialData,
      refetchOnWindowFocus: false,
      staleTime: 300000
    };
    $[3] = initialData;
    $[4] = queryKey;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const {
    isLoading: isPageLoading,
    isError,
    data: spaceQuery
  } = useQuery(t6);
  let t7;
  let t8;
  if ($[9] !== spaceSlug) {
    t7 = ["spaceMedia", spaceSlug];
    t8 = async () => {
      const [error_0, result_0] = await client.spaces.spaceMedia.query({
        slug: spaceSlug
      });
      if (error_0) {
        throw error_0;
      }
      return result_0;
    };
    $[9] = spaceSlug;
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  const t9 = Boolean(spaceSlug);
  let t10;
  if ($[12] !== t7 || $[13] !== t8 || $[14] !== t9) {
    t10 = {
      queryKey: t7,
      queryFn: t8,
      enabled: t9
    };
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const {
    data
  } = useQuery(t10);
  const spaceId = spaceQuery?.id;
  const defaultsToBoard = spaceQuery?.config?.defaultLayout === LayoutType.Board;
  let t11;
  if ($[16] !== defaultsToBoard || $[17] !== searchParams) {
    t11 = VIEW_PARAM_IS_BOARD[searchParams?.get("view") ?? ""] ?? defaultsToBoard;
    $[16] = defaultsToBoard;
    $[17] = searchParams;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const isBoardLayout = t11;
  const [boardHistoryOpen, setBoardHistoryOpen] = useState(false);
  let t12;
  if ($[19] !== router) {
    t12 = () => {
      toast("This space has been deleted.", "", "error");
      router.push("/");
    };
    $[19] = router;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== isBoardLayout || $[22] !== spaceId || $[23] !== spaceSlug || $[24] !== t12) {
    t13 = {
      spaceId,
      spaceSlug,
      isBoardLayout,
      onSpaceDeleted: t12
    };
    $[21] = isBoardLayout;
    $[22] = spaceId;
    $[23] = spaceSlug;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  useBoardRealtime(t13);
  let t14;
  if ($[26] !== defaultsToBoard || $[27] !== isBoardLayout || $[28] !== isMember || $[29] !== spaceQuery) {
    t14 = isBoardLayout && defaultsToBoard && spaceQuery ? isMember(spaceQuery) : false;
    $[26] = defaultsToBoard;
    $[27] = isBoardLayout;
    $[28] = isMember;
    $[29] = spaceQuery;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const canSearchCards = t14;
  let t15;
  if ($[31] !== canSearchCards || $[32] !== spaceQuery || $[33] !== spaceSlug) {
    t15 = spaceQuery ? canSearchCards ? boardScope(spaceSlug) : spaceScope(spaceSlug) : null;
    $[31] = canSearchCards;
    $[32] = spaceQuery;
    $[33] = spaceSlug;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  useRegisterSearchScope(t15);
  if (!isPageLoading && !isError && spaceQuery && isBoardLayout) {
    const t16 = data?.uploads;
    let t17;
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = () => setBoardHistoryOpen(true);
      $[35] = t17;
    } else {
      t17 = $[35];
    }
    let t18;
    if ($[36] !== isBoardLayout || $[37] !== isHomePage || $[38] !== spaceQuery || $[39] !== t16) {
      t18 = <BoardTopBanner space={spaceQuery} uploads={t16} isHomePage={isHomePage} isBoardLayout={isBoardLayout} onHistoryClick={t17} />;
      $[36] = isBoardLayout;
      $[37] = isHomePage;
      $[38] = spaceQuery;
      $[39] = t16;
      $[40] = t18;
    } else {
      t18 = $[40];
    }
    let t19;
    if ($[41] !== spaceQuery || $[42] !== spaceSlug) {
      t19 = <BoardLayout spaceSlug={spaceSlug} spaceQuery={spaceQuery} />;
      $[41] = spaceQuery;
      $[42] = spaceSlug;
      $[43] = t19;
    } else {
      t19 = $[43];
    }
    let t20;
    if ($[44] !== boardHistoryOpen || $[45] !== spaceQuery || $[46] !== spaceSlug) {
      t20 = boardHistoryOpen && <BoardHistoryPanel spaceSlug={spaceSlug} space={spaceQuery} onClose={() => setBoardHistoryOpen(false)} />;
      $[44] = boardHistoryOpen;
      $[45] = spaceQuery;
      $[46] = spaceSlug;
      $[47] = t20;
    } else {
      t20 = $[47];
    }
    let t21;
    if ($[48] !== t18 || $[49] !== t19 || $[50] !== t20) {
      t21 = <AppLayout fullWidth={true} topBanner={t18}>{t19}{t20}</AppLayout>;
      $[48] = t18;
      $[49] = t19;
      $[50] = t20;
      $[51] = t21;
    } else {
      t21 = $[51];
    }
    let t22;
    if ($[52] !== spaceId || $[53] !== spaceSlug || $[54] !== t21) {
      t22 = <RunnerProvider spaceSlug={spaceSlug} spaceId={spaceId}>{t21}</RunnerProvider>;
      $[52] = spaceId;
      $[53] = spaceSlug;
      $[54] = t21;
      $[55] = t22;
    } else {
      t22 = $[55];
    }
    return t22;
  }
  let t16;
  if ($[56] !== data?.uploads || $[57] !== isBoardLayout || $[58] !== isEmbed || $[59] !== isHomePage || $[60] !== spaceQuery) {
    t16 = spaceQuery && <div className="space-y-2"><SpaceInviteBanner space={spaceQuery} /><SpaceHeader uploads={data?.uploads} isBoardLayout={isBoardLayout} space={spaceQuery} isHomePage={isHomePage} isEmbed={isEmbed} hasBanner={spaceQuery.membership?.roles.includes(SpaceMemberRoles.INVITED)} /></div>;
    $[56] = data?.uploads;
    $[57] = isBoardLayout;
    $[58] = isEmbed;
    $[59] = isHomePage;
    $[60] = spaceQuery;
    $[61] = t16;
  } else {
    t16 = $[61];
  }
  let t17;
  if ($[62] !== addRule || $[63] !== editRule || $[64] !== isEmbed || $[65] !== isHomePage || $[66] !== spaceQuery) {
    t17 = isEmbed ? undefined : {
      items: isHomePage ? [] : [...(spaceQuery ? [{
        icon: <List />,
        text: "Factiiis",
        component: <FactiiisList slug={spaceQuery?.spaceSlug} space={spaceQuery} />
      }] : []), {
        icon: <Shield />,
        text: "Rules",
        component: spaceQuery ? <Rules space={spaceQuery} addRule={addRule} editRule={editRule} /> : null
      }]
    };
    $[62] = addRule;
    $[63] = editRule;
    $[64] = isEmbed;
    $[65] = isHomePage;
    $[66] = spaceQuery;
    $[67] = t17;
  } else {
    t17 = $[67];
  }
  let t18;
  if ($[68] !== data?.uploads || $[69] !== isEmbed || $[70] !== isHomePage || $[71] !== spaceQuery || $[72] !== spaceSlug) {
    t18 = isEmbed ? undefined : {
      items: isHomePage || !spaceQuery ? [] : [{
        icon: <UserCog />,
        text: "Moderators",
        component: spaceSlug ? <UsersList fetchFunction={cursor => client.spaces.moderators.query({
          slug: spaceSlug,
          cursor
        })} isSpace={true} /> : null
      }, {
        icon: <Image />,
        text: "Media",
        component: <Media data={data?.uploads} />
      }]
    };
    $[68] = data?.uploads;
    $[69] = isEmbed;
    $[70] = isHomePage;
    $[71] = spaceQuery;
    $[72] = spaceSlug;
    $[73] = t18;
  } else {
    t18 = $[73];
  }
  const t19 = !isEmbed;
  let t20;
  if ($[74] !== isMember || $[75] !== spaceQuery) {
    t20 = spaceQuery ? !isMember(spaceQuery) : true;
    $[74] = isMember;
    $[75] = spaceQuery;
    $[76] = t20;
  } else {
    t20 = $[76];
  }
  let t21;
  if ($[77] !== isError || $[78] !== isMember || $[79] !== isPageLoading || $[80] !== localPreferenceState || $[81] !== spaceQuery || $[82] !== spaceSlug) {
    t21 = !isPageLoading && !isError && spaceQuery && <><div className="flex flex-col space-y-2">{spaceQuery.banned === true && <p>You cannot post to this Space since you've been banned.</p>}{!isMember(spaceQuery) && spaceQuery.types.includes(Types.PRIVATE) && !spaceQuery.membership?.roles.includes(SpaceMemberRoles.INVITED) ? <p>You cannot view the posts of this space since it is a private space and you're not a member.</p> : localPreferenceState.postFilterType && spaceSlug && <Posts params={{
          source: "space",
          spaceSlug
        }} disableLink={false} isFullLength={false} />}</div></>;
    $[77] = isError;
    $[78] = isMember;
    $[79] = isPageLoading;
    $[80] = localPreferenceState;
    $[81] = spaceQuery;
    $[82] = spaceSlug;
    $[83] = t21;
  } else {
    t21 = $[83];
  }
  let t22;
  if ($[84] !== t21) {
    t22 = <div className="relative block items-start">{t21}</div>;
    $[84] = t21;
    $[85] = t22;
  } else {
    t22 = $[85];
  }
  let t23;
  if ($[86] !== initialError || $[87] !== isError || $[88] !== isPageLoading || $[89] !== navigatorState || $[90] !== spaceQuery) {
    t23 = !isPageLoading && !spaceQuery && (!navigatorState.isOnline ? <Error error={isError || initialError} loading={false} /> : <><Head><title>Space Not Found - Factiii</title></Head><NoSpaceFound /></>);
    $[86] = initialError;
    $[87] = isError;
    $[88] = isPageLoading;
    $[89] = navigatorState;
    $[90] = spaceQuery;
    $[91] = t23;
  } else {
    t23 = $[91];
  }
  let t24;
  if ($[92] !== isEmbed || $[93] !== spaceSlug || $[94] !== t16 || $[95] !== t17 || $[96] !== t18 || $[97] !== t19 || $[98] !== t20 || $[99] !== t22 || $[100] !== t23) {
    t24 = <AppLayout isEmbed={isEmbed} topBanner={t16} leftSidebar={t17} rightSidebar={t18} showPostRenderOptions={t19} disableNewPost={t20} spaceSlug={spaceSlug}>{t22}{t23}</AppLayout>;
    $[92] = isEmbed;
    $[93] = spaceSlug;
    $[94] = t16;
    $[95] = t17;
    $[96] = t18;
    $[97] = t19;
    $[98] = t20;
    $[99] = t22;
    $[100] = t23;
    $[101] = t24;
  } else {
    t24 = $[101];
  }
  return t24;
};
export default SpacePage;